/**
 * SPA-navigates the already-running app while reproducing the navigation
 * events in-page consumers rely on.
 *
 * Wouter patches history.pushState/replaceState to emit its route signal, but
 * the browser does not emit `hashchange` for those calls. Fragment consumers
 * such as passage/object landing therefore need the explicit event below when
 * their hosting page stays mounted across the route change.
 *
 * Accepts either a browser-rooted path or Wouter's `~/` rooted form.
 */
export function navigateInApp(
  path: string,
  {
    replace = false,
    retriggerHash = false,
  }: { replace?: boolean; retriggerHash?: boolean } = {},
): void {
  const browserPath = path.startsWith("~/") ? path.slice(1) : path;
  const oldUrl = window.location.href;
  // Deliberately NO synthetic popstate: popstate means "the user traversed
  // history", and both the funder workspace scroll restorer and the dialog
  // close-on-back machinery act on it.
  const targetUrl = new URL(browserPath, oldUrl).href;
  if (targetUrl !== oldUrl) {
    if (replace) {
      window.history.replaceState(null, "", browserPath);
    } else {
      window.history.pushState(null, "", browserPath);
    }
  }
  const newUrl = window.location.href;
  const oldHash = oldUrl.includes("#") ? oldUrl.slice(oldUrl.indexOf("#")) : "";
  const newHash = newUrl.includes("#") ? newUrl.slice(newUrl.indexOf("#")) : "";
  // Deliberately WIDER than same-document browser semantics (which fire only
  // when the fragment itself changes): a different SPA path with the same
  // fragment may keep the landing host mounted, so it must re-land too.
  if (
    (oldUrl !== newUrl || retriggerHash) &&
    (oldHash !== "" || newHash !== "")
  ) {
    window.dispatchEvent(
      new HashChangeEvent("hashchange", { oldURL: oldUrl, newURL: newUrl }),
    );
  }
}
